import React, { Fragment, lazy } from 'react';
import { FadeIn } from './FadeIn';
import { Button } from './Button';
import { DigitalDisclaimer } from './DigitalDisclaimer';
import { Zap, UserCheck, ShieldCheck, RefreshCw, Infinity as InfinityIcon, Lock, Check } from 'lucide-react';
interface ProductCard {
  number: string;
  title: string;
  description: string;
  image: string;
  alt: string;
  price: string;
  originalPrice: string;
  buttonText: string;
  url: string;
  highlighted?: boolean;
  premium?: boolean;
  ribbon?: string;
  features?: string[];
}
export function PricingOptions() {
  const products: ProductCard[] = [{
    number: 'PRODUCTO 1',
    title: 'AMAZON FBA DESDE CERO',
    description: 'Aprende a vender en Amazon FBA paso a paso desde cero y descubre cómo crear un negocio online utilizando el sistema logístico de Amazon.',
    image: 'https://cdn.magicpatterns.com/uploads/fRqZGDUfzvBvZ3MhZduMEC/portada_como_vender_en_amazon_comprimida_.png',
    alt: 'Amazon FBA Desde Cero',
    price: '$15.99',
    originalPrice: '$31.65',
    buttonText: 'QUIERO ESTE CURSO',
    url: 'https://43skvt-1y.myshopify.com/products/como-vender-en-amazon-fba-desde-cero-2026-bonos-exclusivos',
    features: ['Cómo funciona Amazon Seller', 'Publicación de productos', 'Investigación de productos', 'Gestión de inventario', 'Optimización de listings', 'Bonos premium incluidos']
  }, {
    number: 'PRODUCTO 2',
    title: 'AMAZON FBM DESDE CERO',
    description: 'Aprende cómo vender en Amazon FBM sin enviar inventario a bodegas FBA y comienza tu negocio con mayor control y flexibilidad.',
    image: 'https://cdn.magicpatterns.com/uploads/j1AcudPF6ryWv1yAHEPiK7/Pottada_FBM.png',
    alt: 'Amazon FBM Desde Cero',
    price: '$16.99',
    originalPrice: '$31.65',
    buttonText: 'QUIERO ESTE CURSO',
    url: 'https://43skvt-1y.myshopify.com/products/aprende-a-vender-en-amazon-fbm-desde-cero',
    features: ['Publicación de productos', 'Gestión manual de pedidos', 'Atención al cliente', 'Estrategias de ventas', 'Optimización básica', 'Bonos premium incluidos']
  }, {
    number: 'PRODUCTO 3',
    title: 'SISTEMA COMPLETO FBA + FBM',
    description: 'Aprende ambos modelos de Amazon paso a paso y descubre cómo vender usando FBA y FBM para tener más oportunidades de crecimiento y escalabilidad.',
    image: 'https://cdn.magicpatterns.com/uploads/m8TmzqT4Ms73kvuSLyKdxi/ChatGPT_Image_16_may_2026,_10_00_02.png',
    alt: 'Sistema Completo Amazon FBA + FBM',
    price: '$27.99',
    originalPrice: '$31.65',
    buttonText: 'QUIERO EL SISTEMA COMPLETO',
    url: 'https://43skvt-1y.myshopify.com/products/sistema-completo-de-ventas-en-amazon-fba-fbm-2026',
    highlighted: true,
    ribbon: '⭐ LA OPCIÓN MÁS ELEGIDA',
    features: ['Amazon FBA desde cero', 'Amazon FBM desde cero', 'Cómo usar Seller Central', 'Publicación de productos', 'Estrategias de ventas', 'Bonos premium incluidos']
  }, {
    number: 'PRODUCTO 4',
    title: 'SISTEMA DE MARCA PRIVADA™',
    description: 'Aprende cómo importar desde China, encontrar proveedores verificables, crear tu propia marca y vender en múltiples plataformas de Ecommerce moderno.',
    image: 'https://cdn.magicpatterns.com/uploads/cSBDejiN45CDRgZdCx7ynL/portada_IMP.png',
    alt: 'Sistema de Marca Privada',
    price: '$36.99',
    originalPrice: '$97',
    buttonText: 'QUIERO CREAR MI MARCA',
    url: 'https://43skvt-1y.myshopify.com/products/sistema-de-marca-privada%E2%84%A2',
    premium: true,
    ribbon: '👑 PRODUCTO PREMIUM',
    features: ['Importación desde China', 'Proveedores verificables', 'Productos virales', 'Inteligencia Artificial aplicada', 'Prompts listos para usar', 'Shopify, TikTok Shop y más', 'Bonos premium incluidos']
  }];
  const trustItems = [{
    Icon: Zap,
    text: 'ACCESO INMEDIATO'
  }, {
    Icon: UserCheck,
    text: 'SIN EXPERIENCIA PREVIA'
  }, {
    Icon: RefreshCw,
    text: 'ACTUALIZADO PARA 2026'
  }];
  const bottomTrust = [{
    Icon: Zap,
    title: 'ACCESO INMEDIATO',
    subtitle: 'Recibe tu acceso al instante'
  }, {
    Icon: Lock,
    title: 'PAGO 100% SEGURO',
    subtitle: 'Tus datos están protegidos'
  }, {
    Icon: ShieldCheck,
    title: 'COMPRA 100% SEGURA',
    subtitle: 'Pago protegido y confiable'
  }, {
    Icon: RefreshCw,
    title: 'ACTUALIZACIONES GRATIS',
    subtitle: 'Contenido siempre actualizado'
  }, {
    Icon: InfinityIcon,
    title: 'ACCESO DE POR VIDA',
    subtitle: 'Aprende a tu propio ritmo'
  }];
  return <section className="bg-brand-beige py-20 md:py-24 px-5 sm:px-6" data-id="element-615">
      <div className="max-w-7xl mx-auto" data-id="element-616">
        {/* Header */}
        <FadeIn data-id="element-617">
          <div className="text-center mb-10 md:mb-12 max-w-3xl mx-auto" data-id="element-618">
            <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl font-bold text-brand-green mb-4 tracking-tight leading-[1.1]" data-id="element-619">
              Elige tu mejor opción para empezar hoy
            </h2>
            <p className="text-base md:text-lg text-brand-text/70 leading-relaxed" data-id="element-620">
              Elige el sistema ideal para ti y comienza a construir tu negocio
              online hoy mismo.
            </p>
          </div>
        </FadeIn>

        {/* Trust pill bar */}
        <FadeIn delay={0.1} data-id="element-621">
          <div className="flex justify-center mb-14 md:mb-16 px-2" data-id="element-622">
            <div className="inline-flex flex-wrap items-center justify-center gap-3 sm:gap-2 bg-brand-green rounded-full px-4 sm:px-6 py-3 shadow-[0_15px_40px_-15px_rgba(28,57,40,0.5)] border border-brand-gold/20" data-id="element-623">
              {trustItems.map(({
              Icon,
              text
            }, i) => <Fragment key={i} data-id="element-624">
                  <div className="flex items-center gap-2 px-2 sm:px-3" data-id="element-625">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-brand-gold/20 border border-brand-gold/40 flex-shrink-0" data-id="element-626">
                      <Icon className="w-3 h-3 text-brand-gold" strokeWidth={2.5} data-id="element-627" />
                    </span>
                    <span className="text-[11px] sm:text-xs font-bold tracking-wider text-white whitespace-nowrap" data-id="element-628">
                      {text}
                    </span>
                  </div>
                  {i < trustItems.length - 1 && <span className="hidden sm:block w-px h-4 bg-white/15" data-id="element-629"></span>}
                </Fragment>)}
            </div>
          </div>
        </FadeIn>

        {/* Product cards grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-7 lg:gap-6 mb-14 items-stretch" data-id="element-630">
          {products.map((product, index) => {
          const isDark = product.highlighted || product.premium;
          return <FadeIn key={index} delay={0.08 * index} data-id="element-631">
                <div className="relative pt-5 h-full" data-id="element-632">
                  {/* Outer premium glow for 3 and 4 */}
                  {product.highlighted && <div className="absolute -inset-2 bg-brand-green/15 blur-2xl rounded-[2rem] pointer-events-none" data-id="element-633"></div>}
                  {product.premium && <div className="absolute -inset-2 bg-brand-gold/20 blur-2xl rounded-[2rem] pointer-events-none" data-id="element-634"></div>}

                  {/* Floating badge */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 z-20" data-id="element-635">
                    <div className={`px-5 py-1.5 rounded-full text-[10px] sm:text-xs font-bold tracking-[0.15em] uppercase shadow-lg whitespace-nowrap ${product.premium ? 'bg-brand-gold text-brand-green ring-2 ring-brand-green/10' : product.highlighted ? 'bg-brand-gold text-brand-green ring-2 ring-white/40' : 'bg-brand-green text-white ring-1 ring-brand-gold/30'}`} data-id="element-636">
                      {product.number}
                    </div>
                  </div>

                  {/* Card */}
                  <div className={`relative rounded-3xl h-full flex flex-col overflow-hidden transition-all duration-300 ${product.highlighted ? 'bg-gradient-to-br from-brand-green via-[#102b1e] to-brand-green border-2 border-brand-gold/50 shadow-[0_30px_80px_-20px_rgba(28,57,40,0.6)]' : product.premium ? 'bg-gradient-to-br from-brand-green via-[#0f2a1c] to-brand-green border-2 border-brand-gold shadow-[0_30px_80px_-20px_rgba(200,169,106,0.55)]' : 'bg-white border border-brand-text/8 shadow-[0_8px_30px_-10px_rgba(0,0,0,0.08)]'}`} data-id="element-637">
                    {/* Top gold accent line for dark cards */}
                    {isDark && <div className={`absolute top-0 left-0 right-0 h-1 ${product.premium ? 'bg-gradient-to-r from-brand-gold/40 via-brand-gold to-brand-gold/40' : 'bg-gradient-to-r from-transparent via-brand-gold to-transparent'}`} data-id="element-638"></div>}

                    {/* Image */}
                    <div className={`pt-10 px-5 pb-3 flex items-center justify-center ${isDark ? 'bg-gradient-to-b from-white/8 to-transparent' : 'bg-gradient-to-b from-brand-beige/40 to-transparent'}`} data-id="element-639">
                      <img src={product.image} alt={product.alt} loading="lazy" className="w-full h-auto max-h-[280px] object-contain drop-shadow-2xl" data-id="element-640" />
                    </div>

                    {/* Content */}
                    <div className="px-6 sm:px-7 pb-7 pt-3 flex flex-col flex-1" data-id="element-641">
                      {/* Ribbon — inline pill above title */}
                      {product.ribbon && <div className="flex justify-center mb-3" data-id="element-642">
                          <span className={`inline-flex items-center px-3 py-1 text-[9px] sm:text-[10px] font-bold tracking-[0.15em] uppercase rounded-full shadow-sm whitespace-nowrap bg-brand-gold text-brand-green`} data-id="element-643">
                            {product.ribbon}
                          </span>
                        </div>}

                      {/* Title */}
                      <h3 className={`text-base sm:text-lg font-bold text-center mb-3 leading-tight tracking-tight ${isDark ? 'text-white' : 'text-brand-green'}`} data-id="element-644">
                        {product.title}
                      </h3>

                      {/* Description */}
                      <p className={`text-sm text-center leading-relaxed mb-5 ${isDark ? 'text-white/70' : 'text-brand-text/65'}`} data-id="element-645">
                        {product.description}
                      </p>

                      {/* Feature list */}
                      {product.features && <div className={`mb-6 rounded-xl border p-4 space-y-2 ${product.premium ? 'border-brand-gold/25 bg-white/5' : product.highlighted ? 'border-white/10 bg-white/5' : 'border-brand-text/10 bg-brand-beige/40'}`} data-id="element-646">
                          {product.features.map((f, i) => <div key={i} className="flex items-start gap-2" data-id="element-647">
                              <span className={`inline-flex items-center justify-center w-4 h-4 rounded-full flex-shrink-0 mt-0.5 ${isDark ? product.premium ? 'bg-brand-gold/20' : 'bg-brand-gold/15' : 'bg-brand-green/10'}`} data-id="element-648">
                                <Check className={`w-2.5 h-2.5 ${isDark ? 'text-brand-gold' : 'text-brand-green'}`} strokeWidth={3.5} data-id="element-649" />
                              </span>
                              <span className={`text-[12px] sm:text-[13px] font-medium leading-snug ${isDark ? 'text-white/90' : 'text-brand-text/80'}`} data-id="element-650">
                                {f}
                              </span>
                            </div>)}
                        </div>}

                      {/* Price */}
                      <div className="text-center mb-5 mt-auto" data-id="element-651">
                        <div className="flex items-baseline justify-center gap-3 mb-1" data-id="element-652">
                          <p className={`text-4xl sm:text-5xl font-bold tracking-tight leading-none ${isDark ? 'text-white' : 'text-brand-green'}`} data-id="element-653">
                            {product.price}
                          </p>
                          <p className={`text-lg sm:text-xl line-through font-semibold ${isDark ? 'text-white/35' : 'text-brand-text/40'}`} data-id="element-654">
                            {product.originalPrice}
                          </p>
                        </div>
                        <p className={`text-xs mt-1 ${isDark ? 'text-white/50' : 'text-brand-text/50'}`} data-id="element-655">
                          Pago único
                        </p>
                      </div>

                      {/* Button */}
                      <Button onClick={() => window.open(product.url, '_blank', 'noopener,noreferrer')} className={`w-full text-sm sm:text-[0.95rem] font-bold py-4 ${product.premium ? 'bg-brand-gold hover:bg-brand-gold/90 text-brand-green shadow-lg shadow-brand-gold/30' : product.highlighted ? 'bg-brand-gold hover:bg-brand-gold/90 text-brand-green shadow-lg shadow-brand-gold/25' : ''}`} data-id="element-656">
                        {product.buttonText}
                      </Button>
                    </div>
                  </div>
                </div>
              </FadeIn>;
        })}
        </div>

        {/* Digital products disclaimer */}
        <FadeIn delay={0.35} data-id="element-657">
          <DigitalDisclaimer variant="dark" className="mb-10 text-center" data-id="element-658" />
        </FadeIn>

        {/* Bottom trust strip */}
        <FadeIn delay={0.4} data-id="element-659">
          <div className="bg-white border border-brand-text/8 rounded-2xl shadow-[0_8px_30px_-10px_rgba(0,0,0,0.06)] p-5 sm:p-7" data-id="element-660">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5 sm:gap-4" data-id="element-661">
              {bottomTrust.map(({
              Icon,
              title,
              subtitle
            }, i) => <div key={i} className="flex items-center gap-3 min-w-0" data-id="element-662">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-brand-green/8 border border-brand-green/15 flex-shrink-0" data-id="element-663">
                    <Icon className="w-4 h-4 text-brand-green" strokeWidth={2} data-id="element-664" />
                  </span>
                  <div className="min-w-0" data-id="element-665">
                    <p className="text-[11px] sm:text-xs font-bold text-brand-green tracking-wide uppercase leading-tight" data-id="element-666">
                      {title}
                    </p>
                    <p className="text-[10px] sm:text-[11px] text-brand-text/55 leading-tight mt-0.5 truncate" data-id="element-667">
                      {subtitle}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
        </FadeIn>
      </div>
    </section>;
}